import { useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { getApiUrl } from "../utils/api";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaCompass, FaPlus, FaUsers } from "react-icons/fa";
import { UserContext } from "../context/UserContext";
import { useChat } from "../context/ChatContext";
import { usePaginatedTab } from "../hooks/usePaginatedTab";
import ProfileTabLoadMore from "./ProfileTabLoadMore";

function applyGroupCountUpdate(groups, groupId, updater) {
  const id = String(groupId);
  return groups.map((g) =>
    String(g._id) === id && g.isMember ? { ...g, ...updater(g) } : g
  );
}

export default function SidebarGroupsSection({ onLinkClick, className = "" }) {
  const formId = useId();
  const { user } = useContext(UserContext);
  const { socket } = useChat();
  const navigate = useNavigate();
  const location = useLocation();
  const activeGroupId = location.pathname.match(/^\/groups\/([^/]+)/)?.[1] || null;
  const lastBumpRef = useRef({});
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupTagline, setGroupTagline] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [joinPolicy, setJoinPolicy] = useState("approval");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [sidebarView, setSidebarView] = useState("mine");

  const fetchExploreGroupsPage = useCallback(
    async (page, limit) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (user) params.set("discover", "true");

      const res = await fetch(getApiUrl(`/api/groups?${params}`), {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return { items: data.groups || [], pagination: data.pagination };
    },
    [user]
  );

  const exploreFeed = usePaginatedTab({
    enabled: sidebarView === "discover",
    resetKey: `${user?._id || "guest"}-explore`,
    pageSize: 10,
    fetchPage: fetchExploreGroupsPage,
    scrollRootSelectors: [".app-sidebar-scroll", ".app-sidebar-explore-panel"],
  });

  const exploreGroups = exploreFeed.items;

  const loadGroups = useCallback(async ({ silent = false } = {}) => {
    if (!user) {
      setGroups([]);
      setLoading(false);
      return;
    }

    try {
      if (!silent) setLoading(true);
      const res = await fetch(getApiUrl("/api/groups/mine"), {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load groups:", err);
      if (!silent) setGroups([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user]);

  const bumpGroupCount = useCallback(
    (groupId, delta = 1) => {
      const key = String(groupId);
      const onGroupPage = location.pathname === `/groups/${key}`;
      if (onGroupPage && delta > 0) return;

      const now = Date.now();
      if (delta > 0 && now - (lastBumpRef.current[key] || 0) < 1500) return;
      if (delta > 0) lastBumpRef.current[key] = now;

      setGroups((prev) =>
        applyGroupCountUpdate(prev, key, (g) => ({
          newPostCount: Math.min(
            99,
            Math.max(0, (g.newPostCount || 0) + delta)
          ),
        }))
      );
    },
    [location.pathname]
  );

  const clearGroupCount = useCallback((groupId) => {
    setGroups((prev) =>
      applyGroupCountUpdate(prev, groupId, () => ({ newPostCount: 0 }))
    );
  }, []);

  const loadExploreGroups = exploreFeed.reload;

  useEffect(() => {
    loadGroups();
  }, [loadGroups, user?._id]);

  useEffect(() => {
    const onGroupsUpdated = (e) => {
      const detail = e.detail || {};
      if (detail.groupId !== undefined) {
        if (detail.newPostCount === 0) {
          clearGroupCount(detail.groupId);
          return;
        }
        if (detail.delta) {
          bumpGroupCount(detail.groupId, detail.delta);
        }
      }
      if (!detail.skipFetch) {
        loadGroups({ silent: true });
        if (sidebarView === "discover") loadExploreGroups();
      }
    };

    const onGroupNewPost = (e) => {
      const groupId = e.detail?.groupId;
      if (groupId) bumpGroupCount(groupId, 1);
    };

    window.addEventListener("groups-updated", onGroupsUpdated);
    window.addEventListener("group-new-post", onGroupNewPost);
    return () => {
      window.removeEventListener("groups-updated", onGroupsUpdated);
      window.removeEventListener("group-new-post", onGroupNewPost);
    };
  }, [loadGroups, loadExploreGroups, sidebarView, bumpGroupCount, clearGroupCount]);

  useEffect(() => {
    if (!socket) return;

    const onNewPost = (payload) => {
      if (payload?.groupId) bumpGroupCount(payload.groupId, 1);
    };

    const onNotification = (notif) => {
      if (notif?.type === "group_poem_share" && notif?.meta?.groupId) {
        bumpGroupCount(notif.meta.groupId, 1);
      }
      if (
        notif?.type === "group_join_request_response" &&
        notif?.meta?.groupId
      ) {
        loadGroups({ silent: true });
      }
    };

    socket.on("group_new_post", onNewPost);
    socket.on("notification", onNotification);
    return () => {
      socket.off("group_new_post", onNewPost);
      socket.off("notification", onNotification);
    };
  }, [socket, bumpGroupCount, loadGroups]);

  useEffect(() => {
    if (!showCreateModal) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showCreateModal]);

  const resetCreateForm = () => {
    setGroupName("");
    setGroupTagline("");
    setGroupDescription("");
    setJoinPolicy("approval");
    setCreateError("");
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login");
      return;
    }

    const name = groupName.trim();
    if (name.length < 2) {
      setCreateError("Group name must be at least 2 characters.");
      return;
    }

    setCreating(true);
    setCreateError("");
    try {
      const res = await fetch(getApiUrl("/api/groups"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name,
          tagline: groupTagline.trim(),
          description: groupDescription.trim(),
          joinPolicy,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to create group");
      }

      setShowCreateModal(false);
      resetCreateForm();
      await loadGroups({ silent: true });
      onLinkClick?.();
      navigate(`/groups/${data._id}`);
    } catch (err) {
      setCreateError(err.message || "Failed to create group.");
    } finally {
      setCreating(false);
    }
  };

  const switchToDiscover = () => {
    setSidebarView("discover");
  };

  const openCreateModal = () => {
    if (!user) {
      navigate("/login");
      onLinkClick?.();
      return;
    }
    resetCreateForm();
    setShowCreateModal(true);
  };

  const handleGroupClick = () => {
    onLinkClick?.();
  };

  return (
    <div className={className}>
      <div>
        <div className="app-sidebar-groups-header">
          <h2 className="app-sidebar-heading mb-0">
            <FaUsers className="app-sidebar-heading-icon" aria-hidden="true" />
            Groups
          </h2>
          <div className="app-sidebar-groups-header__links" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={sidebarView === "mine"}
              className={`app-sidebar-groups-header__link${
                sidebarView === "mine" ? " app-sidebar-groups-header__link--active" : ""
              }`}
              onClick={() => setSidebarView("mine")}
            >
              My groups
            </button>
            <span className="text-[#ccc] text-[0.55rem]" aria-hidden="true">
              |
            </span>
            <button
              type="button"
              role="tab"
              aria-selected={sidebarView === "discover"}
              className={`app-sidebar-groups-header__link${
                sidebarView === "discover" ? " app-sidebar-groups-header__link--active" : ""
              }`}
              onClick={switchToDiscover}
            >
              Discover groups
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={openCreateModal}
          className="app-sidebar-create-group w-full mb-3"
        >
          <FaPlus className="w-3 h-3" aria-hidden="true" />
          <span>Create group</span>
        </button>

        {sidebarView === "mine" ? (
          <>
            {user && !loading && groups.length > 0 && (
              <p className="app-sidebar-my-groups-label">My groups</p>
            )}
            {!user ? (
              <p className="text-sm text-[#777777] px-1 leading-relaxed">
                Log in to see your groups.
              </p>
            ) : loading ? (
              <p className="text-sm text-[#777777] px-1">Loading groups...</p>
            ) : groups.length === 0 ? (
              <p className="text-sm text-[#777777] px-1 leading-relaxed">
                You&apos;re not in any groups yet. Try Discover groups.
              </p>
            ) : (
              <ul className="app-sidebar-groups">
                {groups.map((group) => (
                  <li key={group._id}>
                    <Link
                      to={`/groups/${group._id}`}
                      className={`app-sidebar-group-item app-sidebar-group-item--link${
                        String(group._id) === String(activeGroupId)
                          ? " app-sidebar-group-item--active"
                          : ""
                      }`}
                      title={group.description || group.name}
                      onClick={handleGroupClick}
                    >
                      <span className="app-sidebar-group-item__label">
                        <FaUsers
                          className="app-sidebar-group-item__icon"
                          aria-hidden="true"
                        />
                        <span className="app-sidebar-group-item__name">{group.name}</span>
                      </span>
                      {group.newPostCount > 0 && (
                        <span
                          className="app-sidebar-group-item__new"
                          aria-label={`${group.newPostCount} new post${group.newPostCount === 1 ? "" : "s"}`}
                        >
                          {group.newPostCount > 99 ? "99+" : group.newPostCount}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <div className="app-sidebar-explore-panel app-sidebar-explore-panel--open">
            {exploreFeed.loading ? (
              <p className="text-sm text-[#777777] px-1">Loading...</p>
            ) : exploreGroups.length === 0 ? (
              <p className="text-sm text-[#777777] px-1 leading-relaxed">
                {user
                  ? "No other groups to explore right now."
                  : "Log in to explore groups."}
              </p>
            ) : (
              <>
              <ul className="app-sidebar-groups app-sidebar-groups--explore">
                {exploreGroups.map((group) => (
                  <li key={group._id}>
                    <Link
                      to={`/groups/${group._id}`}
                      className="app-sidebar-group-item app-sidebar-group-item--link"
                      title={group.description || group.name}
                      onClick={handleGroupClick}
                    >
                      <span className="app-sidebar-group-item__label">
                        <FaUsers
                          className="app-sidebar-group-item__icon"
                          aria-hidden="true"
                        />
                        <span className="app-sidebar-group-item__name">{group.name}</span>
                      </span>
                      <span className="app-sidebar-group-item__count">
                        {group.memberCount}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              <ProfileTabLoadMore
                compact
                sentinelRef={exploreFeed.sentinelRef}
                loadingMore={exploreFeed.loadingMore}
                hasNextPage={exploreFeed.pagination.hasNextPage}
                onLoadMore={exploreFeed.loadMore}
                itemCount={exploreGroups.length}
              />
              </>
            )}
          </div>
        )}

        {sidebarView === "mine" && user && groups.length > 0 && (
          <button
            type="button"
            className="app-sidebar-explore-btn"
            onClick={switchToDiscover}
          >
            <FaCompass aria-hidden="true" />
            Explore all groups
          </button>
        )}

      </div>

      {showCreateModal &&
        createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#333333]/50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !creating) {
              setShowCreateModal(false);
            }
          }}
        >
          <div
            className="group-modal w-full max-w-md"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`create-group-title${formId}`}
          >
            <div className="group-modal__accent" aria-hidden="true" />
            <div className="group-modal__body">
              <h2 id={`create-group-title${formId}`} className="group-modal__title">
                Create a group
              </h2>
              <p className="group-modal__subtitle">
                Gather poets around a theme, workshop, or shared voice.
              </p>
              <form onSubmit={handleCreateGroup} className="space-y-4">
                <div>
                  <label
                    htmlFor={`group-name${formId}`}
                    className="block text-sm font-semibold text-[#333333] mb-1"
                  >
                    Group name
                  </label>
                  <input
                    id={`group-name${formId}`}
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="e.g. Evening Verse"
                    className="peer-home-input w-full px-3 py-2 text-sm"
                    maxLength={80}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label
                    htmlFor={`group-tagline${formId}`}
                    className="block text-sm font-semibold text-[#333333] mb-1"
                  >
                    Tagline{" "}
                    <span className="font-normal text-[#777777]">(optional)</span>
                  </label>
                  <input
                    id={`group-tagline${formId}`}
                    type="text"
                    value={groupTagline}
                    onChange={(e) => setGroupTagline(e.target.value)}
                    placeholder="e.g. Evening poetry"
                    className="peer-home-input w-full px-3 py-2 text-sm"
                    maxLength={120}
                  />
                </div>
                <div>
                  <label
                    htmlFor={`group-description${formId}`}
                    className="block text-sm font-semibold text-[#333333] mb-1"
                  >
                    Description{" "}
                    <span className="font-normal text-[#777777]">(optional)</span>
                  </label>
                  <textarea
                    id={`group-description${formId}`}
                    value={groupDescription}
                    onChange={(e) => setGroupDescription(e.target.value)}
                    placeholder="What is this group about?"
                    className="peer-home-input w-full px-3 py-2 text-sm resize-y min-h-[80px]"
                    maxLength={500}
                  />
                </div>
                <fieldset className="space-y-2">
                  <legend className="block text-sm font-semibold text-[#333333] mb-1">
                    Who can join?
                  </legend>
                  <label className="flex items-start gap-2 text-sm text-[#333333] cursor-pointer">
                    <input
                      type="radio"
                      name={`join-policy${formId}`}
                      value="approval"
                      checked={joinPolicy === "approval"}
                      onChange={() => setJoinPolicy("approval")}
                      className="mt-1"
                    />
                    <span>
                      <span className="font-medium">Approval required</span>
                      <span className="block text-[#777777] text-xs mt-0.5">
                        You review each join request before they become a member.
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-[#333333] cursor-pointer">
                    <input
                      type="radio"
                      name={`join-policy${formId}`}
                      value="open"
                      checked={joinPolicy === "open"}
                      onChange={() => setJoinPolicy("open")}
                      className="mt-1"
                    />
                    <span>
                      <span className="font-medium">Open group</span>
                      <span className="block text-[#777777] text-xs mt-0.5">
                        Any logged-in poet can join instantly without approval.
                      </span>
                    </span>
                  </label>
                </fieldset>
                {createError && (
                  <p className="text-sm text-red-700">{createError}</p>
                )}
                <div className="flex gap-2 justify-end pt-1">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    disabled={creating}
                    className="peer-home-btn-secondary disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="peer-home-btn-primary disabled:opacity-50"
                  >
                    {creating ? "Creating..." : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
